/**
 * Pure helpers for parsing the CHANGELOG markdown into version entries and
 * per-version content sections. Kept dependency-free so they can be unit
 * tested without importing the (ESM-only) markdown renderer.
 */

/** A changelog version headline. */
export interface VersionEntry {
  version: string;
  date: string;
}

/** A changelog version with its rendered body. */
export interface VersionSection extends VersionEntry {
  body: string;
}

// Matches headings like: `## [1.19.0] — 2026-07-03`
const VERSION_HEADING = /^## \[([^\]]+)\](?:\s*[—–-]\s*(.+))?/gm;

/**
 * Extract the ordered list of versions from a changelog document.
 */
export const parseVersions = (markdown: string): VersionEntry[] => {
  const entries: VersionEntry[] = [];
  const regex = new RegExp(VERSION_HEADING);
  let match = regex.exec(markdown);

  while (match !== null) {
    entries.push({ version: match[1], date: match[2]?.trim() || "" });
    match = regex.exec(markdown);
  }

  return entries;
};

/**
 * Split a changelog document into per-version sections, each carrying the body
 * markdown between its heading and the next version heading.
 */
export const parseVersionSections = (markdown: string): VersionSection[] => {
  const regex = new RegExp(VERSION_HEADING);
  const positions: { version: string; date: string; start: number }[] = [];
  let match = regex.exec(markdown);

  while (match !== null) {
    positions.push({
      version: match[1],
      date: match[2]?.trim() || "",
      start: match.index
    });
    match = regex.exec(markdown);
  }

  return positions.map((position, index) => {
    const end = index + 1 < positions.length ? positions[index + 1].start : markdown.length;
    const body = markdown
      .slice(position.start, end)
      .replace(/^## \[[^\]]+\].*\n/, "")
      .trim();

    return { version: position.version, date: position.date, body };
  });
};

/** Kind of change the Novidades tab groups items by, from the CHANGELOG section titles */
export type ChangeKind = "added" | "improved" | "fixed" | "breaking" | "other";

/** One bullet of a CHANGELOG version: "- **Title** — text", with optional sub-bullets */
export interface ChangelogItem {
  title: string;
  text: string;
  details: string[];
}

/** One "### Section" of a CHANGELOG version */
export interface ChangelogSection {
  kind: ChangeKind;
  title: string;
  items: ChangelogItem[];
}

/** One "## [X.Y.Z] — date" block of the CHANGELOG, split into sections and items */
export interface ChangelogVersion extends VersionSection {
  sections: ChangelogSection[];
}

const KIND_BY_TITLE: Record<string, ChangeKind> = {
  adicionado: "added",
  novidades: "added",
  melhorado: "improved",
  alterado: "improved",
  melhorias: "improved",
  corrigido: "fixed",
  correções: "fixed",
  quebras: "breaking"
};

/**
 * Splits a version body into its sections and items. Lines that do not fit the expected shape are
 * appended to the previous item, so a wrapped bullet keeps its text.
 * @param body Markdown between two version headings
 * @returns The sections found, in file order
 */
const parseSections = (body: string): ChangelogSection[] => {
  const sections: ChangelogSection[] = [];
  let section: ChangelogSection | null = null;
  let item: ChangelogItem | null = null;

  body.split(/\r?\n/).forEach((line) => {
    const heading = /^###\s+(.+)$/.exec(line);
    const bullet = /^- (.+)$/.exec(line);
    const detail = /^\s{2,}- (.+)$/.exec(line);

    if (heading) {
      const title = heading[1].trim();

      section = { kind: KIND_BY_TITLE[title.toLowerCase()] || "other", title, items: [] };
      sections.push(section);
      item = null;
    } else if (bullet && section) {
      const named = /^\*\*(.+?)\*\*\s*[—–-]\s*(.+)$/.exec(bullet[1]);

      item = named ? { title: named[1], text: named[2], details: [] } : { title: "", text: bullet[1], details: [] };
      section.items.push(item);
    } else if (detail && item) {
      item.details.push(detail[1]);
    } else if (item && line.trim() && !line.trim().startsWith("---")) {
      item.text = `${item.text} ${line.trim()}`;
    }
  });

  return sections;
};

/**
 * Parses the CHANGELOG into versions with their sections and items, newest first as in the file.
 * @param markdown The whole CHANGELOG.md
 */
export const parseChangelog = (markdown: string): ChangelogVersion[] => parseVersionSections(markdown)
  .map((section) => ({ ...section, sections: parseSections(section.body) }));

/**
 * Compares two semantic versions ("1.19.10" > "1.19.9").
 * @returns Negative, zero or positive, like Array.prototype.sort expects
 */
export const compareVersions = (a: string, b: string): number => {
  const left = a.split(".").map(Number);
  const right = b.split(".").map(Number);

  for (let part = 0; part < 3; part += 1) {
    const diff = (left[part] || 0) - (right[part] || 0);

    if (diff !== 0) return diff;
  }

  return 0;
};

/** The version line of a version: "1.19.6" → "1.19" */
export const versionLine = (version: string): string => version.split(".").slice(0, 2).join(".");

/**
 * Lists the version lines an interval crosses, considering only versions that exist.
 * @param versions Every version in the CHANGELOG
 * @param from First version of the interval (either order works)
 * @param to Last version of the interval
 */
export const linesBetween = (versions: string[], from: string, to: string): string[] => {
  const [low, high] = compareVersions(from, to) <= 0 ? [from, to] : [to, from];
  const lines = versions
    .filter((version) => compareVersions(version, low) >= 0 && compareVersions(version, high) <= 0)
    .map(versionLine);

  return Array.from(new Set(lines)).sort((a, b) => compareVersions(`${a}.0`, `${b}.0`));
};
